import React from 'react';
import { motion } from "framer-motion";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Globe, Shield, Zap, Target, Users, Award } from "lucide-react";

const AboutUs = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <span className="text-sm text-slate-400">Our Mission & Vision</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Pioneering the Future of <br />
            <span className="gradient-text">Global Payments</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            At Luxkgate, we believe that moving money should be as seamless and secure as moving data. We provide the enterprise infrastructure to make that a reality.
          </p>
        </motion.div>

        {/* Story Section */}
        <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">Built for scale, designed for reliability</h2>
            <div className="space-y-4 text-slate-400">
              <p>
                Founded by veterans of the financial technology industry, Luxkgate was created to solve the complex challenges faced by modern engines, resellers, and merchants.
              </p>
              <p>
                We saw that existing payment gateways were often plagued by legacy infrastructure, slow processing times, and rigid compliance architectures. We built our platform from the ground up to handle massive transaction volumes with zero compromise on security.
              </p>
              <p>
                Today, we process over $2 billion monthly and empower businesses across 180+ countries to expand their global footprint effortlessly.
              </p>
            </div>
          </motion.div>
          <motion.div
             initial={{ opacity: 0, x: 20 }}
             animate={{ opacity: 1, x: 0 }}
             transition={{ duration: 0.6, delay: 0.4 }}
             className="relative"
          >
             <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-violet-500/20 rounded-3xl blur-3xl mix-blend-screen" />
             <Card className="bg-slate-900/50 border-slate-800/50 relative z-10 overflow-hidden">
               <CardContent className="p-8">
                 <div className="grid grid-cols-2 gap-8">
                   <div>
                     <div className="text-4xl font-bold text-white mb-2">2020</div>
                     <div className="text-slate-500 text-sm">Year Founded</div>
                   </div>
                   <div>
                     <div className="text-4xl font-bold text-blue-400 mb-2">180+</div>
                     <div className="text-slate-500 text-sm">Countries Served</div>
                   </div>
                   <div>
                     <div className="text-4xl font-bold text-violet-400 mb-2">99.99%</div>
                     <div className="text-slate-500 text-sm">Uptime SLA</div>
                   </div>
                   <div>
                     <div className="text-4xl font-bold text-emerald-400 mb-2">150+</div>
                     <div className="text-slate-500 text-sm">Team Members</div>
                   </div>
                 </div>
               </CardContent>
             </Card>
          </motion.div>
        </div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-white mb-4">Our Core Values</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            The principles that guide our team and shape our products.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Shield,
              title: "Security First",
              description: "We never compromise on the safety of our clients' funds and data. Being PCI DSS Level 1 compliant is just the baseline."
            },
            {
              icon: Zap,
              title: "High Performance",
              description: "Speed matters. We constantly optimize our infrastructure to relentlessly shave milliseconds off every transaction."
            },
            {
              icon: Globe,
              title: "Global Mindset",
              description: "Commerce knows no borders. We build solutions designed to work flawlessly in any currency and jurisdiction."
            },
            {
              icon: Target,
              title: "Absolute Precision",
              description: "Financial systems require perfection. We maintain rigorous standards for code quality and numerical accuracy."
            },
            {
              icon: Users,
              title: "Customer Centric",
              description: "We succeed only when our engines, resellers, and merchants succeed. Support is treated as a feature, not an afterthought."
            },
            {
              icon: Award,
              title: "Continuous Innovation",
              description: "The payment landscape evolves rapidly. We stay ahead by embracing new tech and pioneering modern best practices."
            }
          ].map((value, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
            >
              <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-colors h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-slate-800/50 flex items-center justify-center mb-4">
                    <value.icon className="h-6 w-6 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{value.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default AboutUs;